import React, { useContext, useRef } from 'react';
import { store } from 'components/Store';
import { Link, useLocation } from 'react-router-dom';
import TagsLists from 'components/Dashboard/TagsList';
import useRememberPosition from 'components/Dashboard/hooks/useRememberPosition';
import changeLabelOfContent from 'components/Dashboard/ParliamentaryRecord/ChangeLabelOfContent';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import normalize from 'components/Dashboard/utilities/normalizeString';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import useItemController from 'components/Dashboard/ItemController/useItemController';
import GeneralItemActions from 'components/Dashboard/ItemController/GeneralItemActions';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import usePositionBodySnippet from 'components/Dashboard/hooks/usePositionBodySnippet';
import ContentTypeBadge from 'components/Dashboard/components/ContentTypeBadge';
import PriorityBadge from 'components/Dashboard/ItemController/PriorityBadge';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import time from 'utils/time';
import RatingUpdateBadge from 'components/Dashboard/PriorityUpdates/RatingUpdateBadge';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import preventLink from 'components/Dashboard/TagsList/prevenLink';

const CommitteePublicationItem = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const location = useLocation();

  const { search } = state;
  const { contentResults } = useContentResults();
  const { list, activeSearch } = search;
  const { CommitteePublications } = contentResults;
  const { currentId } = CommitteePublications ?? {};

  const {
    index,
    item,
    scrollBarRef,
    itemOnclick,
    embedShowTags,
    explanations,
    feedItemFlags: propsFeedItemFlags,
    array,
    itemFromWidget,
    notShowShare,
    showHighlights,
    listView,
    showContentTypeBadge,
    thumbDownProps,
    tagsFixedPosition,
    itemActionsProps = {},
    zIndex,
    onlyEmbargoedTag,
    tooltipText,
    showPriorityBadge,
    setActiveUpdate,
  } = props;
  const feedItemFlags = propsFeedItemFlags ?? CommitteePublications?.feedItemFlags ?? [];

  const { dashboardSection } = useMainSectionParameter();

  const itemClass = () => {
    switch (item.house) {
      case 'commons':
        return 'legislation-commons';
      case 'lords':
        return 'legislation-lords';
      default:
        return 'legislation-grey';
    }
  };

  const titleBodyComponent = useRef(null);
  const { bodySnippet } = usePositionBodySnippet({ titleBodyComponent, item });

  const { unseenConditional, locationDescriptor, priorityBadgeCondition, rateUpdatesMode } = useItemController({
    item,
    index,
    propsFeedItemFlags,
    itemFromWidget,
    listView,
    thumbDownProps,
    showPriorityBadge,
  });

  const { rememberPosition } = useRememberPosition();

  const highlightConditional =
    getUrlParam('search') ||
    getUrlParam('or') ||
    getUrlParam('topic-id') ||
    location.pathname.includes('/stakeholders') ||
    location.pathname.startsWith('/embed') ||
    location.pathname.startsWith('/search-results') ||
    showHighlights;

  return (
    <>
      <Link
        to={locationDescriptor(`/committee-publication/${item.id}/${normalize(item.title)}`)}
        className={`content-item-link`}
        style={{ zIndex: zIndex ?? (array ? array.length - index : 1) }}
        onClick={(e) => {
          if (rateUpdatesMode && setActiveUpdate) {
            preventLink(e);
            setActiveUpdate({ ...item, importanceRating: feedItemFlags?.[index]?.importanceRating });
            return;
          }
          rememberPosition('CommitteePublications', scrollBarRef, index);
          if (itemOnclick) {
            itemOnclick(e);
          }
        }}
        data-tooltip-content={tooltipText}
        data-tooltip-id={'general-tooltip'}
        data-tooltip-class-name='header-tooltip'
        data-tooltip-place='top'
      >
        <li
          className={`my-2 ${currentId === item.id ? 'active' : ''} ${
            unseenConditional ? 'unseen-item' : ''
          } adjustment-unseen-dot-parliamentary`}
        >
          <div className='content-item'>
            {unseenConditional && (
              <>
                <span className='unseen-dot' />
              </>
            )}
            <div className='py-3 px-4 parliamentary-item-padding'>
              <div className='d-flex align-items-baseline'>
                <div className='flex-grow-1 pr-1 pr-sm-0'>
                  {showContentTypeBadge ? (
                    <div className='d-flex align-items-baseline'>
                      <div className='mt-1 specific-bottom-margin-12 flex-grow-1 text-light-blue blue-icon content-type-badge'>
                        <ContentTypeBadge objectType={item.type} name={'Committee publication'} />
                      </div>
                      <div className={`d-flex align-items-baseline`}>
                        {priorityBadgeCondition && <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} />}
                        {!notShowShare && (
                          <GeneralItemActions
                            item={item}
                            index={index}
                            thumbDownProps={thumbDownProps}
                            itemActionsProps={itemActionsProps}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <h4 className={`mb-2 content-item-title mb-1`}>{item.title}</h4>
                  )}
                  {showContentTypeBadge && <h4 className={`mb-2 content-item-title mb-1`}>{item.title}</h4>}
                  <div className={`w-100`}>
                    <p
                      className={`heading-date mb-0 ${
                        highlightConditional || dashboardSection ? '' : 'information-date'
                      } ${dashboardSection ? 'item-size-text' : ''}`}
                    >
                      {(item.house || item.contentType) && (
                        <>
                          <span className={`${itemClass()}`}>{`${
                            item.house && item.house !== 'n/a'
                              ? item.house.charAt(0).toUpperCase()
                              : changeLabelOfContent(item.contentType)
                          }${item.house && item.house !== 'n/a' ? item.house.slice(1) : ''}`}</span>
                          <span className='dot'></span>
                        </>
                      )}
                      {item.committee ? (
                        <>
                          {item.committee}
                          <span className='dot'></span>
                        </>
                      ) : (
                        ''
                      )}
                      {item.publicationType ? (
                        <>
                          {item.publicationType}
                          <span className='dot'></span>
                        </>
                      ) : (
                        ''
                      )}
                      <span className='date'>{time(item.dateTime).formatLongDate()}</span>
                    </p>
                  </div>
                </div>
                {!notShowShare && !showContentTypeBadge && (
                  <div className={`flex-centered`}>
                    {rateUpdatesMode ? (
                      <>
                        {feedItemFlags?.[index]?.importanceRating && (
                          <RatingUpdateBadge
                            item={{ rating: TransformFromPascalCase(feedItemFlags?.[index]?.importanceRating) }}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        {priorityBadgeCondition && <PriorityBadge item={item} feedItemFlags={feedItemFlags[index]} />}
                        <GeneralItemActions
                          item={item}
                          index={index}
                          thumbDownProps={thumbDownProps}
                          itemActionsProps={itemActionsProps}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>

              {highlightConditional && (
                <div className='row mx-0'>
                  <div className='legislation-title w-100 px-0'>
                    <p className='title-body mt-2 mb-0 position-relative' ref={titleBodyComponent}>
                      {!!item.contributorName && (
                        <>
                          <strong>{item.contributorName}: </strong>
                        </>
                      )}
                      <span dangerouslySetInnerHTML={createMarkup(bodySnippet)} />
                    </p>
                    {((list !== null && activeSearch && !itemFromWidget) || embedShowTags) && (
                      <TagsLists
                        feedItemFlags={feedItemFlags}
                        explanations={explanations ?? CommitteePublications?.explanations}
                        index={index}
                        title={'Parliamentary record'}
                        section={'CommitteePublications'}
                        tagsFixedPosition={tagsFixedPosition}
                        scrollBarRef={scrollBarRef}
                        onlyEmbargoedTag={onlyEmbargoedTag}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </li>
      </Link>
    </>
  );
};

export default CommitteePublicationItem;
